import React, { useState } from 'react';
import { Modal, Header, Form, Input, Button, Message, Icon, Dimmer, Loader } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { fetchTimClientData, type TimClientData } from '../../services/timData/timDataService';
import { formatCnpjProgressive, cleanCnpj } from '../../services/utils/stringUtils';
import TimClientDataCards from './TimClientDataCards';
import { DesignSystem } from '../../styles/designSystem';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal aberto pelo botão flutuante "Dados TIM": busca por CNPJ, mostra resumo do cliente +
 * linhas telefônicas, e permite abrir o modal de Novo Contrato pré-preenchido com o CNPJ.
 * Ver ideias/consulta-dados-tim-cnpj-renegociacao.md secção 5.5.
 */
const TimDataSearchModal: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [cnpjInput, setCnpjInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<TimClientData | null>(null);
  const [searchedCnpj, setSearchedCnpj] = useState('');

  const handleSearch = async () => {
    const cleaned = cleanCnpj(cnpjInput);
    if (cleaned.length !== 14) {
      setError('Digite um CNPJ válido (14 dígitos).');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await fetchTimClientData(cleaned);
      setSearchedCnpj(cleaned);
      if (data.found) {
        setResult(data);
      } else {
        setError('Nenhum dado TIM encontrado para este CNPJ.');
      }
    } catch (err) {
      console.error('Erro ao consultar dados TIM:', err);
      setError('Não foi possível consultar os dados TIM agora. Tente novamente em alguns instantes.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClose = () => {
    setCnpjInput('');
    setResult(null);
    setError('');
    setSearchedCnpj('');
    onClose();
  };

  const handleCreateRenegotiationContract = () => {
    const cnpjToPrefill = searchedCnpj || cleanCnpj(cnpjInput);
    handleClose();
    navigate('/contracts');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openContractModal', { detail: { cnpj: cnpjToPrefill } }));
    }, 100);
  };

  const hasNoLines = !!result && (result.linhas ?? []).length === 0;

  return (
    <Modal open={open} onClose={handleClose} size="small" closeIcon style={{ maxWidth: '600px' }}>
      <Modal.Header
        style={{
          background: DesignSystem.components.modal.header.background,
          color: 'white',
          padding: '20px 24px',
          borderBottom: 'none',
        }}
      >
        <Header as="h2" style={{ color: 'white', margin: 0 }}>
          <Icon name="database" style={{ color: 'white' }} />
          Dados TIM por CNPJ
        </Header>
      </Modal.Header>

      <Modal.Content style={{ padding: '24px', backgroundColor: '#f8f9fa' }}>
        <Dimmer active={loading} inverted>
          <Loader content="Consultando dados TIM..." />
        </Dimmer>

        <Form>
          <Form.Field>
            <label style={{ color: '#2c3e50', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>CNPJ</label>
            <Input
              placeholder="00.000.000/0000-00"
              value={formatCnpjProgressive(cnpjInput)}
              onChange={(e) => setCnpjInput(cleanCnpj(e.target.value))}
              onKeyPress={handleKeyPress}
              disabled={loading}
              icon="search"
              iconPosition="left"
              fluid
              size="large"
              maxLength={18}
              style={{ borderRadius: '8px' }}
            />
          </Form.Field>

          <Button
            primary
            fluid
            onClick={handleSearch}
            loading={loading}
            disabled={loading || cleanCnpj(cnpjInput).length !== 14}
            style={{
              borderRadius: '8px',
              padding: '10px 0',
              fontWeight: 600,
              background: DesignSystem.components.modal.header.background,
              color: 'white',
              marginTop: '4px',
            }}
          >
            <Icon name="search" />
            Buscar
          </Button>
        </Form>

        {error && (
          <Message warning style={{ marginTop: '20px' }}>
            <Icon name="warning sign" />
            {error}
          </Message>
        )}

        {result && (
          <div style={{ marginTop: '20px' }}>
            <TimClientDataCards data={result} emptyLinesWarning />
          </div>
        )}
      </Modal.Content>

      <Modal.Actions
        style={{
          padding: '16px 24px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}
      >
        <Button onClick={handleClose} disabled={loading} style={{ borderRadius: '8px', padding: '10px 20px' }}>
          Fechar
        </Button>
        <Button
          disabled={!result || hasNoLines}
          onClick={handleCreateRenegotiationContract}
          style={{
            ...DesignSystem.components.button.primary,
            padding: '10px 24px',
          }}
        >
          <Icon name="file alternate" />
          Criar contrato de renegociação
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TimDataSearchModal;
